import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { useManagedOrg } from '@/hooks/useManagedOrg'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { MetricTile, MetricBar } from '@/components/MetricTile'
import { Avatar } from '@/components/Avatar'
import type {
  OrgMetrics,
  LearnerSummaryRow,
  LeaderboardEntry,
  BadgeDistributionEntry,
  EpisodePerformanceEntry,
} from '@/domain/analytics'

// P2 management dashboard. Renders for org_admin, teacher, and super_admin
// only (routed with ProtectedRoute allowedRoles — see App.tsx). The orgId
// driving every query comes exclusively from useManagedOrg(), which pins
// non-super-admins to their own organization — this component never
// receives or trusts an orgId from anywhere else, so cross-org data cannot
// reach the screen (P2 privacy requirement).
export function OrgDashboardPage() {
  const { currentUser } = useAuth()
  const { organizations, orgAnalytics } = useServices()
  const { orgId, orgs, isSuperAdmin, selectedOrgId, setSelectedOrgId, loadingOrgs } = useManagedOrg()
  const isTeacher = currentUser?.role === 'teacher'

  const [orgName, setOrgName] = useState('')
  const [metrics, setMetrics] = useState<OrgMetrics | null>(null)
  const [attentionRows, setAttentionRows] = useState<LearnerSummaryRow[]>([])
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [badgeDistribution, setBadgeDistribution] = useState<BadgeDistributionEntry[]>([])
  const [episodePerformance, setEpisodePerformance] = useState<EpisodePerformanceEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    const roleFilter = isTeacher ? (['school_student'] as const) : undefined

    Promise.all([
      organizations.getById(orgId),
      orgAnalytics.getOrgMetrics(orgId),
      orgAnalytics.listLearnerRows(orgId, roleFilter ? [...roleFilter] : undefined),
      orgAnalytics.getLeaderboard(orgId, 5),
      orgAnalytics.getBadgeDistribution(orgId),
      orgAnalytics.getEpisodePerformance(orgId),
    ]).then(([org, m, rows, board, badgeDist, epPerf]) => {
      setOrgName(org?.name ?? orgId)
      setMetrics(m)
      setAttentionRows(
        rows
          .filter((r) => r.status === 'needs_attention' || r.status === 'not_started')
          .sort((a, b) => a.episodesCompleted - b.episodesCompleted)
          .slice(0, 6)
      )
      setLeaderboard(board)
      setBadgeDistribution(badgeDist)
      setEpisodePerformance(epPerf)
      setLoading(false)
    })
  }, [orgId, organizations, orgAnalytics, isTeacher])

  if (!currentUser) return null
  if (isSuperAdmin && loadingOrgs) return <LoadingSpinner label="Loading organizations…" />
  if (!orgId) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-white">No organization selected</h1>
        <p className="mt-3 text-white/60">There are no seeded organizations to view yet.</p>
      </div>
    )
  }
  if (loading || !metrics) return <LoadingSpinner label="Loading dashboard…" />

  const weakEpisodes = episodePerformance
    .filter((e) => e.passRatePercent !== null)
    .sort((a, b) => (a.passRatePercent ?? 0) - (b.passRatePercent ?? 0))
    .slice(0, 3)

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <span className="cyber-pill bg-teal-500/15 text-teal-300 ring-1 ring-teal-500/30">
            {isTeacher ? 'Teacher view' : 'Organization management'}
          </span>
          <h1 className="mt-3 font-display text-3xl font-bold text-white">
            {orgName} — {isTeacher ? 'Class Overview' : 'Organization Dashboard'}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/60">
            {isTeacher
              ? "Your students' cybersecurity learning progress at a glance — episode completion, quiz results, and badges earned."
              : "A quick read on where your organization's cybersecurity awareness training stands: who's engaged, who's behind, and how the training itself is performing."}
          </p>
        </div>

        {isSuperAdmin && (
          <div className="cyber-panel flex items-center gap-2 p-3">
            <label className="text-xs text-white/50" htmlFor="org-switcher">
              Viewing organization
            </label>
            <select
              id="org-switcher"
              value={selectedOrgId ?? ''}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="rounded-lg border border-white/10 bg-navy-900 px-2 py-1 text-sm text-white"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile label={isTeacher ? 'Students' : 'Total Learners'} value={metrics.totalLearners} accent="text-teal-300" />
        <MetricTile label="Active Learners" value={metrics.activeLearners} accent="text-amber-300" />
        <MetricTile label="Total Cyber Points" value={metrics.totalCyberPoints} accent="gradient-text" />
        <MetricTile label="Learners Needing Attention" value={metrics.learnersNeedingAttention} accent="text-coral-500" />
        <MetricTile label="Badges Earned" value={metrics.totalBadgesEarned} accent="text-white" />
        <MetricTile label="Certificates Issued" value={metrics.certificatesIssued} accent="text-white" />
        <MetricTile
          label="Avg Quiz Score"
          value={metrics.averageQuizScorePercent !== null ? `${metrics.averageQuizScorePercent}%` : '—'}
          accent="text-white"
        />
        <MetricTile
          label="Quiz Pass Rate"
          value={metrics.quizPassRatePercent !== null ? `${metrics.quizPassRatePercent}%` : '—'}
          accent="text-white"
        />
      </section>

      <section className="mt-6 cyber-panel grid gap-6 p-6 sm:grid-cols-2">
        <MetricBar label="Learners who finished the full track" percent={metrics.learnerCompletionRatePercent} accent="bg-emerald-400" />
        <MetricBar label="Overall episode completion" percent={metrics.episodeCompletionRatePercent} accent="bg-teal-400" />
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="cyber-panel p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-white">Learners needing attention</h2>
            <Link to="/team/learners" className="text-sm font-medium text-teal-300 hover:text-teal-200">
              View all learners →
            </Link>
          </div>
          {attentionRows.length === 0 ? (
            <p className="mt-4 text-sm text-white/60">
              Everyone in view is either on track or has finished the training. Nothing needs follow-up right now.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {attentionRows.map((r) => (
                <div key={r.userId} className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
                  <Avatar name={r.displayName} color={r.avatarColor} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{r.displayName}</p>
                    <p className="text-xs text-white/50">
                      {r.status === 'not_started'
                        ? 'Has not started any episode yet'
                        : `${r.episodesCompleted}/${r.episodesTotal} episodes watched · ${r.quizzesPassed}/${r.quizzesAttempted} quizzes passed`}
                    </p>
                  </div>
                  <span className="cyber-pill bg-coral-500/15 text-coral-500 ring-1 ring-coral-500/30 shrink-0">
                    {r.status === 'not_started' ? 'Not started' : 'Needs support'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cyber-panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-white">Top learners</h2>
            <Link to="/leaderboard" className="text-sm font-medium text-teal-300 hover:text-teal-200">
              Full leaderboard →
            </Link>
          </div>
          {leaderboard.length === 0 ? (
            <p className="mt-4 text-sm text-white/60">No Cyber Points earned yet.</p>
          ) : (
            <ol className="mt-4 space-y-2">
              {leaderboard.map((e) => (
                <li key={e.userId} className="flex items-center gap-3 rounded-xl bg-white/5 p-2.5">
                  <span className="w-5 text-center text-sm font-display font-bold text-white/40">{e.rank}</span>
                  <Avatar name={e.displayName} color={e.avatarColor} size={28} />
                  <span className="min-w-0 flex-1 truncate text-sm text-white">{e.displayName}</span>
                  <span className="text-sm font-semibold text-teal-300">{e.xpTotal} XP</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="cyber-panel p-6">
          <h2 className="font-display font-semibold text-white">Badge distribution</h2>
          <div className="mt-4 space-y-3">
            {badgeDistribution.map((b) => (
              <div key={b.badgeKey} className="flex items-center gap-3">
                <span className="text-xl">{b.iconEmoji}</span>
                <span className="w-40 shrink-0 truncate text-sm text-white/80">{b.name}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-indigo-400"
                    style={{
                      width: metrics.totalLearners > 0 ? `${Math.round((b.learnersEarned / metrics.totalLearners) * 100)}%` : '0%',
                    }}
                  />
                </div>
                <span className="w-8 shrink-0 text-right text-sm font-semibold text-white">{b.learnersEarned}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="cyber-panel p-6">
          <h2 className="font-display font-semibold text-white">Common weak areas</h2>
          <p className="mt-1 text-xs text-white/50">Episodes with the lowest quiz pass rate in this organization.</p>
          {weakEpisodes.length === 0 ? (
            <p className="mt-4 text-sm text-white/60">Not enough quiz attempts yet to identify weak areas.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {weakEpisodes.map((ep) => (
                <MetricBar
                  key={ep.episodeId}
                  label={`${ep.title} (${ep.attempts} attempt${ep.attempts === 1 ? '' : 's'})`}
                  percent={ep.passRatePercent ?? 0}
                  accent="bg-amber-400"
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
